/**
 * Wörf görsel standardı.
 *
 * Product photos arrive with very different white margins and proportions, so a tall bottle
 * fills its frame while a tin looks lost. For every image inside [data-worf-fit] (and Horizon's
 * product card galleries) this script measures the product's real outline on a tiny copy of the
 * photo, then scales and shifts the image (CSS `scale` / `translate`, so it composes with any
 * theme `transform`) until every product fills the same share of its frame and stands on the
 * same baseline.
 *
 * Frame tuning per container via CSS custom properties:
 *   --worf-fit-h    share of the frame height the product may use (default 0.84)
 *   --worf-fit-w    share of the frame width the product may use (default 0.8)
 *   --worf-fit-base where the product's bottom edge sits, from the top (default 0.93)
 *
 * Measurements are cached per image path in sessionStorage. If an image can't be read
 * (cross-origin without CORS, decode error) it simply keeps object-fit: contain.
 */
const SELECTOR = '[data-worf-fit] img, .card-gallery img';
const CACHE_KEY = 'worf-fit-v1';
const SAMPLE_WIDTH = 120;

let cache = {};
try {
  cache = JSON.parse(sessionStorage.getItem(CACHE_KEY) || '{}');
} catch {
  cache = {};
}

const pending = new Map();
const boxes = new WeakMap();

function save() {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* storage unavailable: measurements are simply redone next page */
  }
}

function sourceOf(img) {
  return img.currentSrc || img.src || '';
}

function keyOf(src) {
  try {
    return new URL(src, location.href).pathname;
  } catch {
    return src;
  }
}

function sampleUrl(src) {
  const url = new URL(src, location.href);
  url.searchParams.set('width', String(SAMPLE_WIDTH));
  url.searchParams.delete('height');
  url.searchParams.delete('crop');
  return url.href;
}

/** Bounding box of the non-white pixels, as fractions of the image, plus the image ratio. */
function measure(src) {
  const key = keyOf(src);
  if (key in cache) return Promise.resolve(cache[key]);
  if (pending.has(key)) return pending.get(key);

  const job = new Promise((resolve) => {
    const probe = new Image();
    probe.crossOrigin = 'anonymous';
    probe.decoding = 'async';
    probe.onerror = () => resolve(null);
    probe.onload = () => {
      try {
        const w = probe.naturalWidth;
        const h = probe.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(probe, 0, 0);
        const data = ctx.getImageData(0, 0, w, h).data;
        const rows = new Uint16Array(h);
        const cols = new Uint16Array(w);

        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            if (data[i + 3] < 24) continue;
            const lo = Math.min(data[i], data[i + 1], data[i + 2]);
            const hi = Math.max(data[i], data[i + 1], data[i + 2]);
            if (lo > 236 && hi - lo < 14) continue;
            rows[y]++;
            cols[x]++;
          }
        }

        const minRow = Math.max(1, Math.round(w * 0.012));
        const minCol = Math.max(1, Math.round(h * 0.012));
        let y0 = 0;
        while (y0 < h && rows[y0] < minRow) y0++;
        let y1 = h - 1;
        while (y1 > y0 && rows[y1] < minRow) y1--;
        let x0 = 0;
        while (x0 < w && cols[x0] < minCol) x0++;
        let x1 = w - 1;
        while (x1 > x0 && cols[x1] < minCol) x1--;

        let box = null;
        const full = x0 <= 1 && y0 <= 1 && x1 >= w - 2 && y1 >= h - 2;
        if (y1 > y0 && x1 > x0 && !full) {
          box = { x0: x0 / w, y0: y0 / h, x1: (x1 + 1) / w, y1: (y1 + 1) / h, r: w / h };
        }
        cache[key] = box;
        save();
        resolve(box);
      } catch {
        resolve(null);
      }
    };
    probe.src = sampleUrl(src);
  });

  pending.set(key, job);
  return job;
}

function setting(style, name, fallback) {
  const value = parseFloat(style.getPropertyValue(name));
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

/** Scale and shift the (object-fit: contain) image so its product outline fits the frame rules. */
function place(img) {
  const box = boxes.get(img);
  if (!box) return;
  const W = img.clientWidth;
  const H = img.clientHeight;
  if (!W || !H) return;

  const style = getComputedStyle(img);
  const fitH = setting(style, '--worf-fit-h', 0.84);
  const fitW = setting(style, '--worf-fit-w', 0.8);
  const base = setting(style, '--worf-fit-base', 0.93);

  let iw;
  let ih;
  if (box.r > W / H) {
    iw = W;
    ih = W / box.r;
  } else {
    ih = H;
    iw = H * box.r;
  }
  const ix = (W - iw) / 2;
  const iy = (H - ih) / 2;

  const cw = (box.x1 - box.x0) * iw;
  const ch = (box.y1 - box.y0) * ih;
  const cx = ix + ((box.x0 + box.x1) / 2) * iw;
  const cy1 = iy + box.y1 * ih;

  const s = Math.min(Math.max(Math.min((fitH * H) / ch, (fitW * W) / cw), 0.5), 3);
  const tx = -(cx - W / 2) * s;
  const ty = base * H - (H / 2 + (cy1 - H / 2) * s);

  img.style.scale = s.toFixed(4);
  img.style.translate = `${tx.toFixed(1)}px ${ty.toFixed(1)}px`;
  img.classList.add('is-worf-fit');
}

const resizer = new ResizeObserver((entries) => {
  for (const entry of entries) place(entry.target);
});

async function fit(img) {
  if (img.dataset.worfFitDone) return;
  const src = sourceOf(img);
  if (!src || src.startsWith('data:') || src.endsWith('.svg')) return;
  img.dataset.worfFitDone = '1';
  const box = await measure(src);
  if (!box) return;
  boxes.set(img, box);
  place(img);
  resizer.observe(img);
}

function scan(root) {
  root.querySelectorAll?.(SELECTOR).forEach((img) => {
    if (img.complete && sourceOf(img)) {
      fit(img);
    } else {
      img.addEventListener('load', () => fit(img), { once: true });
    }
  });
}

const lazy = new IntersectionObserver(
  (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      lazy.unobserve(entry.target);
      scan(entry.target);
    }
  },
  { rootMargin: '400px 0px' }
);

function watch(root) {
  root.querySelectorAll?.('[data-worf-fit], .card-gallery').forEach((frame) => lazy.observe(frame));
  if (root.matches?.('[data-worf-fit], .card-gallery')) lazy.observe(root);
}

watch(document);
new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) watch(node);
    });
  }
}).observe(document.body, { childList: true, subtree: true });
